import React, { useState, useEffect } from 'react';
import { clinicService } from '../../services/api';

const TemplatesSection = ({ clinicId, onDataChange }) => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isCloning, setIsCloning] = useState(false);

  // Form states
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [defaultNamespace, setDefaultNamespace] = useState('default');
  const [availableNamespaces, setAvailableNamespaces] = useState(['default']);
  const [formData, setFormData] = useState({
    id: '',
    description: '',
    type: 'internal',
    namespace: 'default',
    content: '{}',
    quick_reply_options: '[]',
    list_options: '{}',
    flow_options: '{}'
  });

  // Load templates when component mounts
  useEffect(() => {
    fetchTemplates();
  }, [clinicId]);

  // Filter templates when searchTerm or templates change
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredTemplates(templates);
    } else {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      const filtered = templates.filter(
        (template) =>
          template.id.toLowerCase().includes(lowerCaseSearchTerm) ||
          template.description.toLowerCase().includes(lowerCaseSearchTerm) ||
          template.type.toLowerCase().includes(lowerCaseSearchTerm) ||
          template.namespace.toLowerCase().includes(lowerCaseSearchTerm) ||
          (template.clid && template.clid.toLowerCase().includes(lowerCaseSearchTerm)) ||
          (!template.clid && 'global'.includes(lowerCaseSearchTerm))
      );
      setFilteredTemplates(filtered);
    }
  }, [searchTerm, templates]);


  // Add this useEffect to fetch available namespaces when component mounts
  useEffect(() => {
    const fetchAvailableNamespaces = async () => {
      try {
        const data = await clinicService.getDefaultNamespace(clinicId);
        setDefaultNamespace(data.default_namespace);
        setAvailableNamespaces(data.available_namespaces || ['default']);
        console.log('Default namespace:', data.default_namespace);
        console.log('Available namespaces:', data.available_namespaces);
      } catch (err) {
        console.error('Error fetching available namespaces:', err);
      }
    };

    if (clinicId) {
      fetchAvailableNamespaces();
    }
  }, [clinicId]);

  // Fetch all templates
  const fetchTemplates = async () => {
        setLoading(true);
    try {
        console.log('Loading templates for clinic:', clinicId);
        const data = await clinicService.getClinicTemplates(clinicId);
        console.log('Loaded templates data:', data);
        setTemplates(data || []);
      setError(null);
      } catch (err) {
        console.error('Error loading templates:', err);
        setError('Failed to load templates. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle JSON field changes
  const handleJsonChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  // Handle dialog open/close
  const handleAddDialogOpen = () => {
    setIsCloning(false); // This is creating a new template, not cloning
    setFormData({
      id: '',
      clid: clinicId, // Add clid field
      description: '',
      type: 'internal',
      namespace: defaultNamespace, // Use fetched default namespace
      content: '{}',
      quick_reply_options: '[]',
      list_options: '{}',
      flow_options: '{}'
    });
    setOpenAddDialog(true);
  };

  const handleEditDialogOpen = (template) => {
    setSelectedTemplate(template);
    setFormData({
      id: template.id,
      description: template.description,
      type: template.type,
      namespace: template.namespace,
      content: JSON.stringify(template.content || {}, null, 2),
      quick_reply_options: JSON.stringify(template.quick_reply_options || [], null, 2),
      list_options: JSON.stringify(template.list_options || {}, null, 2),
      flow_options: JSON.stringify(template.flow_options || {}, null, 2)
    });
    setOpenEditDialog(true);
  };

  const handleDeleteDialogOpen = (template) => {
    setSelectedTemplate(template);
    setOpenDeleteDialog(true);
  };

  const handleCloneTemplate = (template) => {
    setIsCloning(true); // This is cloning a template
    // Clone the template by copying all its data
    setFormData({
      id: `${template.id}`, // Use same ID (will be converted to clinic-specific)
      clid: clinicId, // Set to current clinic
      description: `${template.description} (Copy)`, // Add (Copy) to description
      type: template.type,
      namespace: template.namespace, // Use the original template's namespace
      content: JSON.stringify(template.content || {}, null, 2),
      quick_reply_options: JSON.stringify(template.quick_reply_options || [], null, 2),
      list_options: JSON.stringify(template.list_options || {}, null, 2),
      flow_options: JSON.stringify(template.flow_options || {}, null, 2)
    });
    setOpenAddDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenAddDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setSelectedTemplate(null);
    setIsCloning(false); // Reset cloning state
  };

  // CRUD operations
  const handleAddTemplate = async () => {
    try {
      const templateData = {
        ...formData,
        clid: clinicId,
        content: JSON.parse(formData.content),
        quick_reply_options: JSON.parse(formData.quick_reply_options),
        list_options: JSON.parse(formData.list_options),
        flow_options: JSON.parse(formData.flow_options)
      };
      await clinicService.createClinicTemplate(clinicId, templateData);
      handleCloseDialogs();
      fetchTemplates();
    } catch (err) {
      console.error('Error adding template:', err);
      setError('Failed to add template. Please check JSON format.');
    }
  };

  const handleUpdateTemplate = async () => {
    try {
      const templateData = {
        ...formData,
        content: JSON.parse(formData.content),
        quick_reply_options: JSON.parse(formData.quick_reply_options),
        list_options: JSON.parse(formData.list_options),
        flow_options: JSON.parse(formData.flow_options)
      };
      await clinicService.updateClinicTemplate(clinicId, formData.id, templateData);
      handleCloseDialogs();
      fetchTemplates();
    } catch (err) {
      console.error('Error updating template:', err);
      setError('Failed to update template. Please check JSON format.');
    }
  };

  const handleDeleteTemplate = async () => {
    try {
      await clinicService.deleteClinicTemplate(clinicId, selectedTemplate.id);
      handleCloseDialogs();
      fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      setError('Failed to delete template. Please try again.');
    }
  };

  // Get template type display name
  const getTemplateTypeName = (typeValue) => {
    return typeValue.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Clinic Templates</h2>
        <button
          className="bg-[#075e54] hover:bg-[#054c44] text-white px-4 py-2 rounded flex items-center space-x-2"
          onClick={handleAddDialogOpen}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          <span>Add Template</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
          <p>{error}</p>
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-4 relative">
        <div className="flex">
          <div className="relative flex-grow">
            <input
              type="text"
              placeholder="Search templates by ID, description, type, namespace, or clinic ID..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full p-2 pl-10 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
            />
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 absolute left-3 top-2.5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
              </button>
            )}
          </div>
        </div>
        {searchTerm && (
          <div className="mt-2 text-sm text-gray-600">
            Found {filteredTemplates.length} {filteredTemplates.length === 1 ? 'template' : 'templates'} matching "{searchTerm}"
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#075e54]"></div>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="bg-gray-50 p-8 text-center rounded">
          <p className="text-gray-500">
            {templates.length === 0
              ? "No templates found. Add one to get started."
              : "No templates match your search. Try a different search term."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clinic ID</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Namespace</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Languages</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTemplates.map((template) => (
                <tr key={`${template.clid}-${template.id}`}>
                  <td className="px-6 py-4 whitespace-nowrap">{template.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                      {template.clid || 'Global'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{template.description}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{getTemplateTypeName(template.type)}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {template.namespace}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-1">
                      {template.content?.en_US && (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          EN
                </span>
                      )}
                      {template.content?.zh_HK && (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          HK
                          </span>
                      )}
                      {template.content?.zh_CN && (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          CN
                          </span>
                      )}
                        </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex space-x-2 justify-end">
                      {/* Show edit/delete buttons for templates belonging to this clinic */}
                      {template.clid === clinicId ? (
                        <>
                          <button
                            className="text-indigo-600 hover:text-indigo-900"
                            onClick={() => handleEditDialogOpen(template)}
                            title="Edit"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900"
                            onClick={() => handleDeleteDialogOpen(template)}
                            title="Delete"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </>
                      ) : (
                        /* Show clone button for global templates */
                        <button
                          className="text-green-600 hover:text-green-900"
                          onClick={() => handleCloneTemplate(template)}
                          title="Clone Template"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M7 3.5A1.5 1.5 0 018.5 2h3.879a1.5 1.5 0 011.061.44l3.122 3.12A1.5 1.5 0 0117 6.622V12.5a1.5 1.5 0 01-1.5 1.5h-1v-3.379a3 3 0 00-.879-2.121L10.5 5.379A3 3 0 008.379 4.5H7v-1z" />
                            <path d="M4.5 6A1.5 1.5 0 003 7.5v9A1.5 1.5 0 004.5 18h7a1.5 1.5 0 001.5-1.5v-5.879a1.5 1.5 0 00-.44-1.06L9.44 6.439A1.5 1.5 0 008.378 6H4.5z" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Dialog */}
      {openEditDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50" onClick={handleCloseDialogs}>
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Edit Template</h3>

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template ID</label>
                <input
                  type="text"
                  name="id"
                  value={formData.id}
                  className="w-full p-2 border border-gray-300 rounded bg-gray-100 text-gray-600"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Clinic ID</label>
                <input
                  type="text"
                  name="clid"
                  value={clinicId}
                  className="w-full p-2 border border-gray-300 rounded bg-gray-100 text-gray-600"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <input
                  type="text"
                  name="type"
                  value={formData.type}
                  className="w-full p-2 border border-gray-300 rounded bg-gray-100 text-gray-600"
                  readOnly
                />
                <p className="text-xs text-gray-500 mt-1">Template type cannot be changed</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Namespace</label>
              <input
                type="text"
                name="namespace"
                value={formData.namespace}
                className="w-full p-2 border border-gray-300 rounded bg-gray-100 text-gray-600"
                readOnly
              />
              <p className="text-xs text-gray-500 mt-1">Cannot be changed when editing</p>
            </div>

            {/* Content Section */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-4">Content (JSON)</h4>
              <div className="border border-gray-200 rounded p-4">
                <textarea
                  value={formData.content}
                  onChange={(e) => handleJsonChange('content', e.target.value)}
                  rows={8}
                  className="w-full p-2 border border-gray-300 rounded font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                  placeholder='{"en_US": "Hello", "zh_HK": "你好", "zh_CN": "你好"}'
                ></textarea>
              </div>
            </div>

            {/* Quick Reply Options */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-4">Quick Reply Options (JSON)</h4>
              <div className="border border-gray-200 rounded p-4">
                <textarea
                  value={formData.quick_reply_options}
                  onChange={(e) => handleJsonChange('quick_reply_options', e.target.value)}
                  rows={6}
                  className="w-full p-2 border border-gray-300 rounded font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                  placeholder='[{"label": {"en_US": "Confirm", "zh_CN": "確認", "zh_HK": "確認"}, "value": "ok"}]'
                ></textarea>
              </div>
            </div>

            {/* List Options */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-4">List Options (JSON)</h4>
              <div className="border border-gray-200 rounded p-4">
                <textarea
                  value={formData.list_options}
                  onChange={(e) => handleJsonChange('list_options', e.target.value)}
                  rows={12}
                  className="w-full p-2 border border-gray-300 rounded font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                  placeholder='{"sections": {"en_US": [{"rows": [{"id": "00_01::1", "title": "Services"}], "title": "Main Menu"}]}, "button_text": {"en_US": "Main Menu"}}'
                ></textarea>
              </div>
            </div>

            {/* Flow Options */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-4">Flow Options (JSON)</h4>
              <div className="border border-gray-200 rounded p-4">
                <textarea
                  value={formData.flow_options}
                  onChange={(e) => handleJsonChange('flow_options', e.target.value)}
                  rows={8}
                  className="w-full p-2 border border-gray-300 rounded font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                  placeholder='{"en_US": {"flow_cta": "Appointment Request", "flow_name": "STAG_HKSH0000_appointment_v2", "flow_action": "navigate"}}'
                ></textarea>
              </div>
            </div>

            <p className="text-xs text-gray-600 mt-2 mb-4">
              Use {'{{'}{'{variable}'}{'}}'} syntax for dynamic content. Example: "Hello {'{{'}{'{name}'}{'}}'}"
            </p>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={handleCloseDialogs}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateTemplate}
                className="px-4 py-2 bg-[#075e54] text-white rounded hover:bg-[#054c44]"
                type="button"
              >
                Update
              </button>
            </div>
                        </div>
                      </div>
                    )}

      {/* Add Dialog */}
      {openAddDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50" onClick={handleCloseDialogs}>
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">
              {isCloning ? "Clone Template" : "Add New Template"}
            </h3>

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template ID</label>
                {isCloning ? (
                  <input
                    type="text"
                    name="id"
                    value={formData.id}
                    className="w-full p-2 border border-gray-300 rounded bg-gray-100 text-gray-600"
                    readOnly
                  />
                ) : (
                  <input
                    type="text"
                    name="id"
                    value={formData.id}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                    placeholder="Enter template ID"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Clinic ID</label>
                <input
                  type="text"
                  name="clid"
                  value={formData.clid}
                  className="w-full p-2 border border-gray-300 rounded bg-gray-100 text-gray-600"
                  readOnly
                />
                <p className="text-xs text-gray-500 mt-1">Fixed to current clinic</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                {isCloning ? (
                  // When cloning, show read-only input with original template type
                  <input
                    type="text"
                    name="type"
                    value={formData.type}
                    className="w-full p-2 border border-gray-300 rounded bg-gray-100 text-gray-600"
                    readOnly
                  />
                ) : (
                  // When creating new template, show select dropdown
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                  >
                    <option value="internal">Internal</option>
                    <option value="whatsapp_template">WhatsApp Template</option>
                    <option value="whatsapp_list">WhatsApp List</option>
                    <option value="whatsapp_quick_reply">WhatsApp Quick Reply</option>
                    <option value="whatsapp_flow">WhatsApp Flow</option>
                  </select>
                )}
                {isCloning && (
                  <p className="text-xs text-gray-500 mt-1">Template type cannot be changed when cloning</p>
                )}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                placeholder="Enter description"
              />
                  </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Namespace</label>
              {isCloning ? (
                // When cloning, show read-only input with original namespace
                <input
                  type="text"
                  name="namespace"
                  value={formData.namespace}
                  className="w-full p-2 border border-gray-300 rounded bg-gray-100 text-gray-600"
                  readOnly
                />
              ) : (
                // When creating new, show dropdown with available namespaces
                <select
                  name="namespace"
                  value={formData.namespace}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                >
                  {availableNamespaces.map((ns) => (
                    <option key={ns} value={ns}>
                      {ns}
                    </option>
                  ))}
                </select>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {isCloning
                  ? "Original template namespace (cannot be changed when cloning)"
                  : "Available namespaces for this clinic"
                }
              </p>
            </div>

            {/* Content Section */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-4">Content (JSON)</h4>
              <div className="border border-gray-200 rounded p-4">
                <textarea
                  value={formData.content}
                  onChange={(e) => handleJsonChange('content', e.target.value)}
                  rows={8}
                  className="w-full p-2 border border-gray-300 rounded font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                  placeholder='{"en_US": "Hello", "zh_HK": "你好", "zh_CN": "你好"}'
                ></textarea>
              </div>
            </div>

            {/* Quick Reply Options */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-4">Quick Reply Options (JSON)</h4>
              <div className="border border-gray-200 rounded p-4">
                <textarea
                  value={formData.quick_reply_options}
                  onChange={(e) => handleJsonChange('quick_reply_options', e.target.value)}
                  rows={6}
                  className="w-full p-2 border border-gray-300 rounded font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                  placeholder='[{"label": {"en_US": "Confirm", "zh_CN": "確認", "zh_HK": "確認"}, "value": "ok"}]'
                ></textarea>
              </div>
            </div>

            {/* List Options */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-4">List Options (JSON)</h4>
              <div className="border border-gray-200 rounded p-4">
                <textarea
                  value={formData.list_options}
                  onChange={(e) => handleJsonChange('list_options', e.target.value)}
                  rows={12}
                  className="w-full p-2 border border-gray-300 rounded font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                  placeholder='{"sections": {"en_US": [{"rows": [{"id": "00_01::1", "title": "Services"}], "title": "Main Menu"}]}, "button_text": {"en_US": "Main Menu"}}'
                ></textarea>
              </div>
                  </div>

            {/* Flow Options */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-4">Flow Options (JSON)</h4>
              <div className="border border-gray-200 rounded p-4">
                <textarea
                  value={formData.flow_options}
                  onChange={(e) => handleJsonChange('flow_options', e.target.value)}
                  rows={8}
                  className="w-full p-2 border border-gray-300 rounded font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:border-transparent"
                  placeholder='{"en_US": {"flow_cta": "Appointment Request", "flow_name": "STAG_HKSH0000_appointment_v2", "flow_action": "navigate"}}'
                ></textarea>
              </div>
            </div>

            <p className="text-xs text-gray-600 mt-2 mb-4">
              Use {'{{'}{'{variable}'}{'}}'} syntax for dynamic content. Example: "Hello {'{{'}{'{name}'}{'}}'}"
            </p>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={handleCloseDialogs}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleAddTemplate}
                className="px-4 py-2 bg-[#075e54] text-white rounded hover:bg-[#054c44]"
                type="button"
              >
                {isCloning ? "Clone Template" : "Add Template"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Dialog */}
      {openDeleteDialog && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50" onClick={handleCloseDialogs}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Delete Template</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete the template "{selectedTemplate?.description}" (ID: {selectedTemplate?.id})? This action cannot be undone.
            </p>

            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={handleCloseDialogs}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTemplate}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                type="button"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplatesSection;
